import React, { useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ExamContext } from '../contexts/ExamContext';
import { FaLanguage } from 'react-icons/fa';
import { getOptionKey } from '../utils/helpers';

const Review = () => {
  const { questions, answers, language, setLanguage, resetExamState } = useContext(ExamContext);
  const [filter, setFilter] = useState('All'); // 'All', 'Correct', 'Wrong', 'Skipped'
  const [filteredQuestions, setFilteredQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const navigate = useNavigate();

  // --- helpers to keep logic same as Question component ---

  const normalizeKey = (key) => (key || '').toString().trim().toLowerCase();

  useEffect(() => {
    let filtered = questions;

    if (filter === 'Correct') {
      filtered = questions.filter((q) => {
        const userAnswer = answers[q.id];
        if (!userAnswer) return false;
        return normalizeKey(userAnswer) === normalizeKey(q.correct);
      });
    } else if (filter === 'Wrong') {
      filtered = questions.filter((q) => {
        const userAnswer = answers[q.id];
        if (!userAnswer) return false;
        return normalizeKey(userAnswer) !== normalizeKey(q.correct);
      });
    } else if (filter === 'Skipped') {
      filtered = questions.filter((q) => !answers[q.id]);
    }

    setFilteredQuestions(filtered);
    setCurrentIndex(0); // Reset to first in filtered list
  }, [filter, questions, answers]);

  // Scroll to top when question changes (especially helpful on mobile)
  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [currentIndex]);

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'hi' : 'en');
  };

  const handlePrev = () => {
    setCurrentIndex(Math.max(0, currentIndex - 1));
  };

  const handleNext = () => {
    setCurrentIndex(Math.min(filteredQuestions.length - 1, currentIndex + 1));
  };

  if (filteredQuestions.length === 0) {
    return (
      <div className="review-container">
        <h2>Review Questions</h2>
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap', justifyContent: 'center' }}>
          <button
            className="go-results-btn"
            onClick={() => navigate('/results')}
            aria-label="Go to Results"
          >
            Go to Results
          </button>
          <button
            className="go-home-btn"
            onClick={() => {
              if (window.confirm('Are you sure you want to go to Home Page?')) {
                resetExamState();
                navigate('/');
              }
            }}
            aria-label="Go to Home"
          >
            Go to Home
          </button>
        </div>
        <p>No questions match the selected filter.</p>
        <div className="filter-buttons">
          {['All', 'Correct', 'Wrong', 'Skipped'].map((f) => (
            <button key={f} onClick={() => setFilter(f)} className={filter === f ? 'active' : ''}>
              {f}
            </button>
          ))}
        </div>
      </div>
    );
  }

  const q = filteredQuestions[currentIndex];
  const userAnswer = answers[q.id];
  const normalizedUserAnswer = normalizeKey(userAnswer);
  const normalizedCorrect = normalizeKey(q.correct);
  const isCorrect = !!userAnswer && normalizedUserAnswer === normalizedCorrect;

  const userAnswerOption =
    userAnswer && q.options.find((opt) => getOptionKey(opt) === normalizedUserAnswer);
  const correctAnswerOption = q.options.find(
    (opt) => getOptionKey(opt) === normalizedCorrect
  );

  const userAnswerText = userAnswerOption || 'Not Answered';
  const correctAnswerText = correctAnswerOption || 'N/A';

  return (
    <div className="review-container">
      <h2>Review Questions</h2>
      <div className="language-toggle">
        <button onClick={toggleLanguage}>
          <FaLanguage /> Switch to {language === 'en' ? 'Hindi' : 'English'}
        </button>
        <button
          className="go-results-btn"
          onClick={() => navigate('/results')}
          aria-label="Go to Results"
        >
          Go to Results
        </button>
        <button
          className="go-home-btn"
          onClick={() => {
            if (window.confirm('Are you sure you want to go to Home Page?')) {
              resetExamState();
              navigate('/');
            }
          }}
          aria-label="Go to Home"
        >
          Go to Home
        </button>
      </div>
      <div className="filter-buttons">
        {['All', 'Correct', 'Wrong', 'Skipped'].map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={filter === f ? 'active' : ''}>
            {f}
          </button>
        ))}
      </div>
      <div className="question-review">
        <h3>
          Question {currentIndex + 1} of {filteredQuestions.length} ({filter})
        </h3>
        <p className="question-text">
          {language === 'en' ? q.question_en : q.question_hi}
        </p>
        <div className="options-grid">
          {q.options.map((opt, index) => {
            const optionKey = getOptionKey(opt);
            const isUserSelected = optionKey === normalizedUserAnswer;
            const isCorrectOption = optionKey === normalizedCorrect;

            let className = 'option-button';
            if (isCorrectOption) className += ' correct-option';
            if (isUserSelected && !isCorrect) className += ' wrong-selected';
            if (isUserSelected && isCorrect) className += ' correct-selected';

            return (
              <div key={index} className={className}>
                {opt}
              </div>
            );
          })}
        </div>
        <div className="answer-summary">
          <p>
            <strong>Your Answer:</strong> {userAnswerText}
          </p>
          <p>
            <strong>Correct Answer:</strong> {correctAnswerText}
          </p>
        </div>
      </div>
      <div className="review-navigation">
        <button onClick={handlePrev} disabled={currentIndex === 0}>
          Previous
        </button>
        <span>
          {currentIndex + 1} / {filteredQuestions.length}
        </span>
        <button
          onClick={handleNext}
          disabled={currentIndex === filteredQuestions.length - 1}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default Review;
